import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RegistrationDetailModal = ({ registration, onClose, onApprove, onReject, onRequestInfo }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [adminComment, setAdminComment] = useState('');

  const tabs = [
    { id: 'details', label: 'Details', icon: 'User' },
    { id: 'documents', label: 'Documents', icon: 'FileText' },
    { id: 'verification', label: 'Verification', icon: 'CheckSquare' },
    { id: 'notes', label: 'Notes', icon: 'MessageSquare' }
  ];

  const getStatusColor = (status) => {
    const colors = {
      'Pending': 'bg-warning/10 text-warning border-warning/20',
      'Under Review': 'bg-accent/10 text-accent border-accent/20',
      'Verified': 'bg-success/10 text-success border-success/20',
      'Rejected': 'bg-error/10 text-error border-error/20',
      'Approved': 'bg-primary/10 text-primary border-primary/20'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground';
  };

  const verificationChecklist = [
    { id: 1, item: 'Identity verification completed', checked: registration?.verificationStatus === 'Verified' },
    { id: 2, item: 'Contact information verified', checked: registration?.verificationStatus === 'Verified' },
    { id: 3, item: 'Documents uploaded and valid', checked: registration?.documents?.length > 0 },
    { id: 4, item: 'Background check completed', checked: registration?.verificationStatus === 'Verified' }
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-xl border border-border w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-2xl font-heading font-bold text-card-foreground">Registration Details</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-muted transition-smooth"
            aria-label="Close modal"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="flex border-b border-border overflow-x-auto">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-6 py-3 font-medium whitespace-nowrap transition-smooth ${
                activeTab === tab?.id
                  ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={18} />
              {tab?.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'details' && (
            <div className="space-y-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-card-foreground mb-2">{registration?.applicantName}</h3>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(registration?.verificationStatus)}`}>
                      {registration?.verificationStatus}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(registration?.approvalStatus)}`}>
                      {registration?.approvalStatus}
                    </span>
                    {registration?.priority === 'high' && (
                      <span className="px-3 py-1 rounded-full text-sm font-medium bg-error/10 text-error border border-error/20">
                        High Priority
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Registration Type</label>
                    <p className="text-base text-card-foreground">{registration?.registrationType}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Email</label>
                    <p className="text-base text-card-foreground">{registration?.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Phone</label>
                    <p className="text-base text-card-foreground">{registration?.phone}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Submission Date</label>
                    <p className="text-base text-card-foreground">{formatDate(registration?.submissionDate)}</p>
                  </div>
                  {registration?.registrationType === 'Student' && (
                    <>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Institution</label>
                        <p className="text-base text-card-foreground">{registration?.institution}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Course</label>
                        <p className="text-base text-card-foreground">{registration?.course}</p>
                      </div>
                    </>
                  )}
                  {registration?.registrationType === 'Employer' && (
                    <>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Company Size</label>
                        <p className="text-base text-card-foreground">{registration?.companySize}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Industry</label>
                        <p className="text-base text-card-foreground">{registration?.industry}</p>
                      </div>
                    </>
                  )}
                  {registration?.registrationType === 'Institution' && (
                    <>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Institution Type</label>
                        <p className="text-base text-card-foreground">{registration?.institutionType}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Accreditation</label>
                        <p className="text-base text-card-foreground">{registration?.accreditation}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="space-y-4">
              {registration?.documents?.map((doc, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border">
                  <div className="flex items-center gap-3">
                    <Icon name="FileText" size={24} color="var(--color-primary)" />
                    <div>
                      <p className="text-sm font-medium text-card-foreground">{doc}</p>
                      <p className="text-xs text-muted-foreground">Uploaded: {formatDate(registration?.submissionDate)}</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
                    Download
                  </Button>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'verification' && (
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-card-foreground mb-4">Verification Checklist</h4>
              {verificationChecklist?.map((item) => (
                <div key={item?.id} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border border-border">
                  <div className={`w-5 h-5 rounded flex items-center justify-center ${
                    item?.checked ? 'bg-success text-success-foreground' : 'bg-muted border border-border'
                  }`}>
                    {item?.checked && <Icon name="Check" size={14} />}
                  </div>
                  <span className="text-sm text-card-foreground">{item?.item}</span>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-card-foreground mb-2 block">Admin Comments</label>
                <textarea
                  value={adminComment}
                  onChange={(e) => setAdminComment(e?.target?.value)}
                  placeholder="Add notes or comments about this registration..."
                  className="w-full h-32 px-3 py-2 border border-border rounded-lg bg-background text-card-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              {registration?.notes && (
                <div className="p-4 bg-muted/50 rounded-lg border border-border">
                  <p className="text-sm font-medium text-card-foreground mb-1">Existing Notes:</p>
                  <p className="text-sm text-muted-foreground">{registration?.notes}</p>
                </div>
              )}
              <Button variant="default" iconName="Save" iconPosition="left" fullWidth>
                Save Notes
              </Button>
            </div>
          )}
        </div>

        <div className="flex gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose} fullWidth>
            Close
          </Button>
          {registration?.approvalStatus === 'Pending' && (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  onRequestInfo(registration?.id);
                  onClose();
                }}
                iconName="MessageSquare"
                iconPosition="left"
                fullWidth
              >
                Request Info
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  onReject(registration?.id);
                  onClose();
                }}
                iconName="XCircle"
                iconPosition="left"
                fullWidth
              >
                Reject
              </Button>
              <Button
                variant="success"
                onClick={() => {
                  onApprove(registration?.id);
                  onClose();
                }}
                iconName="CheckCircle"
                iconPosition="left"
                fullWidth
              >
                Approve
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegistrationDetailModal;